/**
 * The name vocabulary the Autogenerate buttons draw from.
 *
 * **These are REAL-LOOKING names, by product call on 31 Oct 2026.** That REVERSES the earlier
 * rule — *a generator writing into `rsys_pii` produces obviously synthetic names* — which the
 * personnel generator had followed with `Sample Testrecord ug8mb1`. The reason for the reversal
 * is that a screen full of `Testrow`/`Placeholder` exercises the layout badly: column widths,
 * truncation, the surname-first sort and the two-line header all behave differently under a
 * real name than under a six-letter token, so the generated record stopped resembling the thing
 * the screens are built for.
 *
 * **What was given up, and it is real**: a row in `rsys_pii` generated by this button is no
 * longer distinguishable, by reading it, from a row about a person. Anyone clearing test data
 * out of that store has to go by the record, not by the name. The reasoning and the alternative
 * that lost are in `reference/decisions/pii-satellite.md`.
 *
 * Filipino, because the product is one: the surnames are the common ones, and the MIDDLE name
 * is drawn from the same list as the surname because here a middle name IS the mother's maiden
 * surname rather than a second given name.
 */

import { SALUTATIONS } from './salutations';

/** Drawn for a surname AND for a middle name — a middle name is a maiden surname here. */
export const SAMPLE_SURNAMES: readonly string[] = [
  'Dela Cruz',
  'Santos',
  'Reyes',
  'Bautista',
  'Garcia',
  'Mendoza',
  'Cruz',
  'Ramos',
  'Aquino',
  'Villanueva',
  'Castillo',
  'Flores',
  'Torres',
  'Rivera',
  'Gonzales',
  'Lopez',
  'Fernandez',
  'Domingo',
  'Navarro',
  'Salazar',
  'Pascual',
  'Tolentino',
  'Alvarez',
  'Agustin',
];

export const SAMPLE_GIVEN_NAMES_FEMALE: readonly string[] = [
  'Maria',
  'Ana',
  'Josefina',
  'Cristina',
  'Rosario',
  'Carmela',
  'Liezl',
  'Jocelyn',
  'Grace',
  'Divina',
  'Lourdes',
  'Angeline',
  'Catherine',
  'Rowena',
  'Imelda',
  'Jennifer',
  'Michelle',
  'Sheila',
];

export const SAMPLE_GIVEN_NAMES_MALE: readonly string[] = [
  'Juan',
  'Jose',
  'Antonio',
  'Ramon',
  'Ricardo',
  'Eduardo',
  'Rolando',
  'Manuel',
  'Carlos',
  'Danilo',
  'Reynaldo',
  'Alfredo',
  'Arnel',
  'Ferdinand',
  'Joselito',
  'Noel',
  'Rodel',
  'Emmanuel',
];

function draw(rows: readonly string[]): string {
  return rows[Math.floor(Math.random() * rows.length)];
}

/**
 * A given name.
 *
 * **It takes the sex rather than ignoring it**, because the record carries both and a generator
 * that drew them independently produces a file whose name and sex disagree — which is a defect
 * a reader would report, on a screen that is supposed to look ordinary. A caller with no sex to
 * hand passes nothing and gets either list.
 */
export function sampleGivenName(sex?: 'female' | 'male'): string {
  if (sex === 'female') return draw(SAMPLE_GIVEN_NAMES_FEMALE);
  if (sex === 'male') return draw(SAMPLE_GIVEN_NAMES_MALE);

  return draw(
    Math.random() < 0.5 ? SAMPLE_GIVEN_NAMES_FEMALE : SAMPLE_GIVEN_NAMES_MALE,
  );
}

/** A surname — and the same call supplies a middle name, which here is a maiden surname. */
export function sampleSurname(): string {
  return draw(SAMPLE_SURNAMES);
}

/**
 * A title that can stand in front of the given name drawn for this sex.
 *
 * **It filters `SALUTATIONS` by NAME, never by position.** Reading `SALUTATIONS[1]` for `Ms.`
 * works until somebody reorders the list, and then it silently puts `Mr.` on a female record —
 * the kind of break nothing fails on. The shared list is still the source, so a title this
 * product does not offer cannot be generated.
 *
 * Only the three gendered titles are refused; `Dr.`, `Engr.`, `Atty.` and `Hon.` are neutral
 * and stay in play for both.
 */
export function sampleSalutation(sex: 'female' | 'male'): string {
  const refused = sex === 'male' ? ['Ms.', 'Mrs.'] : ['Mr.'];

  return draw(SALUTATIONS.filter((title) => !refused.includes(title)));
}

/**
 * A generational suffix, and ONLY for a male record.
 *
 * `Jr.` was unconditional while the names were tokens, where it read as one more fixture value.
 * Beside a real given name it stops being neutral — `Maria Santos Jr.` is a record no clerk
 * would enter — so it is drawn only where it fits, and the joined Last name + Suffix control is
 * exercised on roughly half the generations rather than on all of them.
 */
export function sampleSuffix(sex: 'female' | 'male'): string {
  return sex === 'male' ? 'Jr.' : '';
}
